export const COLLECT_MODE_HISTORY = '采集历史数据'
export const COLLECT_MODE_REALTIME = '采集实时数据'

export const COLLECT_MODE_OPTIONS = [
  { label: COLLECT_MODE_HISTORY, value: COLLECT_MODE_HISTORY },
  { label: COLLECT_MODE_REALTIME, value: COLLECT_MODE_REALTIME }
]

const LEGACY_COLLECT_MODE_MAP = {
  history: COLLECT_MODE_HISTORY,
  realtime: COLLECT_MODE_REALTIME,
  采集历史数据: COLLECT_MODE_HISTORY,
  采集实时数据: COLLECT_MODE_REALTIME,
  实时采集: COLLECT_MODE_REALTIME,
  实时: COLLECT_MODE_REALTIME
}

function pad(value) {
  return String(value).padStart(2, '0')
}

export function formatDateTime(date) {
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate())
  ].join('-') + ' ' + [
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds())
  ].join(':')
}

export function normalizeDateTimeForPicker(value) {
  if (value == null || value === '') return ''
  if (value instanceof Date) {
    return formatDateTime(value)
  }
  if (Array.isArray(value) && value.length >= 5) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value
    return formatDateTime(new Date(year, month - 1, day, hour, minute, second))
  }
  let text = String(value).trim()
  if (!text) return ''
  text = text.replace('T', ' ').replace(/\.\d+$/, '')
  if (text.length >= 19) {
    return text.slice(0, 19)
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text + ' 00:00:00'
  }
  return text
}

export function buildDateTimeRange(start, end) {
  const startText = normalizeDateTimeForPicker(start)
  const endText = normalizeDateTimeForPicker(end)
  return startText && endText ? [startText, endText] : []
}

export function toApiDateTime(value) {
  const normalized = normalizeDateTimeForPicker(value)
  return normalized ? normalized.replace(' ', 'T') : value
}

export function defaultRealtimeDateRange() {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  end.setHours(23, 59, 59, 0)
  return [formatDateTime(start), formatDateTime(end)]
}

export function startOfToday(baseDate) {
  const date = baseDate ? new Date(baseDate) : new Date()
  date.setHours(0, 0, 0, 0)
  return date
}

function toDateValue(value) {
  if (value instanceof Date) return value
  const text = normalizeDateTimeForPicker(value)
  if (!text) return null
  const parsed = new Date(text.replace(' ', 'T'))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const COLLECT_RANGE_MAX_MONTHS = 1

/** 按月偏移，月末溢出时收敛到目标月最后一天（如 1-31 加一个月得 2-28） */
function addMonths(date, months) {
  const result = new Date(date)
  const day = result.getDate()
  result.setMonth(result.getMonth() + months)
  if (result.getDate() < day) {
    result.setDate(0)
  }
  return result
}

/**
 * 历史采集只能选昨天（T-1）及以前，实时采集只能选今天及以后；
 * 已选定第一个日期后，另一端限制在该日期前后各一个月内。
 */
export function isCollectDateDisabled(collectMode, date, anchorDate) {
  const today = startOfToday().getTime()
  const time = startOfToday(date).getTime()
  if (isRealtimeCollectMode(collectMode)) {
    if (time < today) return true
  } else if (time >= today) {
    return true
  }
  const anchor = anchorDate ? startOfToday(anchorDate) : null
  if (!anchor) return false
  const earliest = startOfToday(addMonths(anchor, -COLLECT_RANGE_MAX_MONTHS)).getTime()
  const latest = startOfToday(addMonths(anchor, COLLECT_RANGE_MAX_MONTHS)).getTime()
  return time < earliest || time > latest
}

export function buildCollectModePickerOptions(collectMode, getAnchorDate) {
  return {
    disabledDate(date) {
      const anchor = typeof getAnchorDate === 'function' ? getAnchorDate() : null
      return isCollectDateDisabled(collectMode, date, anchor)
    }
  }
}

export function validateCollectDateRange(collectMode, dateRange) {
  if (!Array.isArray(dateRange) || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
    return '请选择采集周期'
  }
  const start = toDateValue(dateRange[0])
  const end = toDateValue(dateRange[1])
  if (!start || !end) {
    return '采集周期格式无效，请选择正确的时间段'
  }
  if (start.getTime() >= end.getTime()) {
    return '开始时间必须早于结束时间，请选择正确的时间段'
  }
  const today = startOfToday()
  if (isRealtimeCollectMode(collectMode)) {
    if (startOfToday(start).getTime() < today.getTime()) {
      return '实时采集只能选择今天及以后的时间，请选择正确的时间段'
    }
    if (startOfToday(end).getTime() <= today.getTime()) {
      return '实时任务的结束时间必须大于当前时间'
    }
    // 开始时间落在今天时结束时间必然也不早于今天，故无需再单独校验开始时间
  } else if (startOfToday(end).getTime() >= today.getTime()) {
    return '历史任务的结束时间最大为昨天（T-1）'
  }
  if (startOfToday(end).getTime() > startOfToday(addMonths(start, COLLECT_RANGE_MAX_MONTHS)).getTime()) {
    const modeName = isRealtimeCollectMode(collectMode) ? '实时采集' : '历史采集'
    return `${modeName}的开始时间与结束时间最多相差 ${COLLECT_RANGE_MAX_MONTHS} 个月，请选择正确的时间段`
  }
  return ''
}

export function normalizeCollectModeValue(value) {
  if (!value) return COLLECT_MODE_HISTORY
  const text = String(value).trim()
  return LEGACY_COLLECT_MODE_MAP[text] || text
}

export function isRealtimeCollectMode(value) {
  return normalizeCollectModeValue(value) === COLLECT_MODE_REALTIME
}

export function formatCollectModeLabel(value) {
  const normalized = normalizeCollectModeValue(value)
  const option = COLLECT_MODE_OPTIONS.find(item => item.value === normalized)
  return option ? option.label : (normalized || value || '—')
}

/** 预估展示：成功给条数与时间，失败给原因与时间，未预估为 — */
export function formatEstimateText(count, at, error) {
  const reason = error == null ? '' : String(error).trim()
  const time = normalizeDateTimeForPicker(at)
  if (reason) {
    return time ? `失败：${reason}（${time}）` : `失败：${reason}`
  }
  if (count == null || count === '') {
    return '—'
  }
  return time ? `${count} 条（${time}）` : `${count} 条`
}
